import {
  createActionGroup,
  createFeature,
  createReducer,
  createSelector,
  on,
  props,
  emptyProps
} from '@ngrx/store';
import {
  zefActionPayload,
  zefActionFailPayload,
  zefActionSuccessPayload
} from '@zerops/zef/core';
import {
  Recipe,
  RecipeCategory,
  RecipeLanguageFramework,
  GitHubRecipe,
  RecipeExtended,
  RecipeEnvironmentParsed
} from '@zerops/models/recipes';
import { RecipeBaseState, StaticContent } from './recipe-base.model';
import { FEATURE_NAME } from './recipe-base.constant';
import { normalize, denormalize } from './recipe-base.utils';

export const RecipeBaseActions = createActionGroup({
  source: '@zerops/zerops/core/recipe-base',
  events: {
    'load recipe': props<{ id: string }>(),

    'load recipes': zefActionPayload<{ key?: string; params?: Record<string, string>}>(),
    'load recipes success': zefActionSuccessPayload<{ key: string; data: Recipe[]; }>(),
    'load recipes fail': zefActionFailPayload,
    'load recipes cancel': props<{ key: string; cacheKey: string }>(),

    'load recipe categories': zefActionPayload<{ key?: string; params?: Record<string, string> }>(),
    'load recipe categories success': zefActionSuccessPayload<{ key: string; data: RecipeCategory[]; }>(),
    'load recipe categories fail': zefActionFailPayload,
    'load recipe categories cancel': props<{ key: string; cacheKey: string }>(),

    'load recipe language frameworks': zefActionPayload<{ key?: string; params?: Record<string, string> }>(),
    'load recipe language frameworks success': zefActionSuccessPayload<{ key: string; data: RecipeLanguageFramework[]; }>(),
    'load recipe language frameworks fail': zefActionFailPayload,
    'load recipe language frameworks cancel': props<{ key: string; cacheKey: string }>(),

    'load github recipe': zefActionPayload<{ url: string; specificYaml?: string }>(),
    'load github recipe success': zefActionSuccessPayload<{ data: GitHubRecipe }>(),
    'load github recipe fail': zefActionFailPayload,
    'load github recipe cancel': props<{ url: string; recipeId: string }>(),

    // Related recipes
    'load related recipes': props<{ recipeId: string | number; limit?: number }>(),
    'load related recipes success': props<{ recipeId: string | number; data: Recipe[] }>(),
    'load related recipes fail': zefActionFailPayload,
    'load related recipes cancel': props<{ recipeId: string | number }>(),

    // Cache invalidation actions
    'invalidate cache': props<{ cacheType: 'recipes' | 'categories' | 'languageFrameworks' | 'github' | 'all'; pattern?: string }>(),
    'clear cache': props<{ cacheType: 'recipes' | 'categories' | 'languageFrameworks' | 'github' | 'all' }>(),

    // Static content actions (environment guides, service type docs from CMS)
    'load static content': emptyProps(),
    'load static content success': props<{ data: StaticContent }>(),
    'load static content fail': zefActionFailPayload,
    'load static content cancel': emptyProps()
  }
});

const initialState: RecipeBaseState = {
  entities: {
    recipes: {},
    categories: {},
    languageFrameworks: {},
    githubRecipes: {},
    relatedRecipes: {}
  },
  slugMaps: {
    recipes: {},
    categories: {},
    languageFrameworks: {},
    githubRecipes: {}
  },
  lists: {
    recipes: {},
    categories: {},
    languageFrameworks: {},
    githubRecipes: {}
  },
  timestamps: {
    recipes: {},
    categories: {},
    languageFrameworks: {},
    githubRecipes: {}
  },
  staticContent: null
};

// Slugify a display name into a URL/key-safe token (lowercase, non-alnum -> `-`,
// trimmed). May yield an empty string for a symbol-only / non-Latin name — see
// `keyOrFallback`, which guarantees a non-empty key.
const slugifyEnvName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// Never return an empty key: an empty `@for` track / `?environment=` value / map
// key collapses every empty-slug env into one (NG0955 + silent disappearance from
// the selector). Fall back to a stable, order-derived key.
const keyOrFallback = (key: string, order: number): string =>
  key || `env-${order}`;

// Disambiguate a key that already exists in `used` by suffixing `-2`, `-3`, …
// Two envs that slugify to the same key (e.g. "1 — Prod!!!" / "1 — Prod???")
// would otherwise overwrite each other in the map — one env silently vanishes
// from the selector / deploy. `used` is mutated to record the resolved key.
export const uniqueEnvKey = (key: string, used: Set<string>): string => {
  if (!used.has(key)) {
    used.add(key);
    return key;
  }
  let suffix = 2;
  let candidate = `${key}-${suffix}`;
  while (used.has(candidate)) {
    suffix += 1;
    candidate = `${key}-${suffix}`;
  }
  used.add(candidate);
  return candidate;
};

// Helper function to parse environment name
export const parseEnvironmentName = (name: string): { order: number; key: string; name: string } => {
  // Match pattern like "1 — Remote (CDE)" or legacy "3_stage" pattern
  const newPatternMatch = name.match(/^(\d+)\s*[—-]\s*(.+)$/);

  if (newPatternMatch) {
    const order = parseInt(newPatternMatch[1], 10);
    const displayName = newPatternMatch[2].trim();
    // Generate a key from the display name (lowercase, spaces to dashes)
    const key = keyOrFallback(slugifyEnvName(displayName), order);
    return { order, key, name: displayName };
  }

  // Legacy pattern support: "3_stage" or "5_highly-available-production"
  const legacyMatch = name.match(/^(\d+)_(.+)$/);
  if (legacyMatch) {
    const order = parseInt(legacyMatch[1], 10);
    const key = keyOrFallback(legacyMatch[2], order);
    // Convert dashes to spaces and capitalize words for display name
    const displayName = legacyMatch[2].replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return { order, key, name: displayName };
  }

  // Fallback for names without recognized pattern
  const fallbackOrder = 999; // Put unprefixed names at the end
  return {
    order: fallbackOrder,
    key: keyOrFallback(slugifyEnvName(name), fallbackOrder),
    name: name
  };
};

const handleLoadRecipesSuccess = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.loadRecipesSuccess>
): RecipeBaseState => {
  const { data, key } = action.data;
  const listKey = key || 'default';

  // Extend recipes with parsed environments
  const extendedData = data.map((recipe) => {
    if (!recipe.sourceData?.environments) {
      return {
        ...recipe,
        _environmentsMap: {}
      } as RecipeExtended;
    }

    const environmentsMap: Record<string, RecipeEnvironmentParsed> = {};
    const parsedEnvironments: RecipeEnvironmentParsed[] = [];
    const usedKeys = new Set<string>();

    recipe.sourceData.environments.forEach((env) => {
      const { order, key, name } = parseEnvironmentName(env.name);
      // Guard duplicate keys so two envs that slugify the same both survive in
      // the map / selector instead of the last one overwriting the first.
      const uniqueKey = uniqueEnvKey(key, usedKeys);
      const parsedEnv: RecipeEnvironmentParsed = {
        ...env,
        _order: order,
        key: uniqueKey,
        name: name
      };
      parsedEnvironments.push(parsedEnv);
      // Use the key (without prefix) as the map key
      environmentsMap[uniqueKey] = parsedEnv;
    });

    // Sort environments by order
    parsedEnvironments.sort((a, b) => a._order - b._order);

    return {
      ...recipe,
      sourceData: {
        ...recipe.sourceData,
        environments: parsedEnvironments
      },
      _environmentsMap: environmentsMap
    } as RecipeExtended;
  });

  const { entities, slugMap, ids } = normalize(
    extendedData,
    state.entities.recipes,
    state.slugMaps.recipes
  );

  return {
    ...state,
    entities: {
      ...state.entities,
      recipes: entities
    },
    slugMaps: {
      ...state.slugMaps,
      recipes: slugMap
    },
    lists: {
      ...state.lists,
      recipes: {
        ...state.lists.recipes,
        [listKey]: ids
      }
    },
    timestamps: {
      ...state.timestamps,
      recipes: {
        ...state.timestamps.recipes,
        [listKey]: Date.now()  // Store current timestamp
      }
    }
  };
};

const handleLoadRecipeCategoriesSuccess = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.loadRecipeCategoriesSuccess>
): RecipeBaseState => {
  const { data, key = 'default' } = action.data;

  const { entities, slugMap, ids } = normalize(
    data,
    state.entities.categories,
    state.slugMaps.categories
  );

  return {
    ...state,
    entities: {
      ...state.entities,
      categories: entities
    },
    slugMaps: {
      ...state.slugMaps,
      categories: slugMap
    },
    lists: {
      ...state.lists,
      categories: {
        ...state.lists.categories,
        [key]: ids
      }
    },
    timestamps: {
      ...state.timestamps,
      categories: {
        ...state.timestamps.categories,
        [key]: Date.now()
      }
    }
  };
};

const handleLoadRecipeLanguageFrameworksSuccess = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.loadRecipeLanguageFrameworksSuccess>
): RecipeBaseState => {
  const { data, key = 'default' } = action.data;

  const { entities, slugMap, ids } = normalize(
    data,
    state.entities.languageFrameworks,
    state.slugMaps.languageFrameworks
  );

  return {
    ...state,
    entities: {
      ...state.entities,
      languageFrameworks: entities
    },
    slugMaps: {
      ...state.slugMaps,
      languageFrameworks: slugMap
    },
    lists: {
      ...state.lists,
      languageFrameworks: {
        ...state.lists.languageFrameworks,
        [key]: ids
      }
    },
    timestamps: {
      ...state.timestamps,
      languageFrameworks: {
        ...state.timestamps.languageFrameworks,
        [key]: Date.now()
      }
    }
  };
};

const handleLoadGitHubRecipeSuccess = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.loadGithubRecipeSuccess>
): RecipeBaseState => {
  const { data: githubRecipe } = action.data;

  return {
    ...state,
    entities: {
      ...state.entities,
      githubRecipes: {
        ...state.entities.githubRecipes,
        [githubRecipe.id]: githubRecipe
      }
    },
    slugMaps: {
      ...state.slugMaps,
      githubRecipes: {
        ...state.slugMaps.githubRecipes,
        [githubRecipe.sourceUrl]: githubRecipe.id
      }
    }
  };
};

const handleLoadRelatedRecipesSuccess = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.loadRelatedRecipesSuccess>
): RecipeBaseState => {
  const { recipeId, data } = action;

  // Merge related recipes into main recipes entity store
  const newRecipes = data.reduce((acc, recipe) => {
    // Only add if not already in store (to preserve extended data)
    if (!state.entities.recipes[recipe.id]) {
      acc[recipe.id] = recipe as RecipeExtended;
    }
    return acc;
  }, { ...state.entities.recipes });

  // Also update slugMaps for new recipes
  const newSlugMaps = data.reduce((acc, recipe) => {
    if (recipe.slug && !acc[recipe.slug]) {
      acc[recipe.slug] = recipe.id;
    }
    return acc;
  }, { ...state.slugMaps.recipes });

  return {
    ...state,
    entities: {
      ...state.entities,
      recipes: newRecipes,
      relatedRecipes: {
        ...state.entities.relatedRecipes,
        [recipeId]: data.map((r) => r.id)
      }
    },
    slugMaps: {
      ...state.slugMaps,
      recipes: newSlugMaps
    }
  };
};

const handleInvalidateCache = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.invalidateCache>
): RecipeBaseState => {
  const { cacheType, pattern } = action;

  if (cacheType === 'all') {
    return {
      ...state,
      lists: {
        recipes: {},
        categories: {},
        languageFrameworks: {},
        githubRecipes: {}
      }
    };
  }

  const clearListsByPattern = (lists: Record<string, any>, pattern?: string) => {
    if (!pattern) return {};

    return Object.keys(lists).reduce((newLists, key) => {
      if (!key.includes(pattern)) {
        newLists[key] = lists[key];
      }
      return newLists;
    }, {} as Record<string, any>);
  };

  switch (cacheType) {
    case 'recipes':
      return {
        ...state,
        lists: {
          ...state.lists,
          recipes: clearListsByPattern(state.lists.recipes, pattern)
        }
      };
    case 'categories':
      return {
        ...state,
        lists: {
          ...state.lists,
          categories: clearListsByPattern(state.lists.categories, pattern)
        }
      };
    case 'languageFrameworks':
      return {
        ...state,
        lists: {
          ...state.lists,
          languageFrameworks: clearListsByPattern(state.lists.languageFrameworks, pattern)
        }
      };
    case 'github':
      return {
        ...state,
        lists: {
          ...state.lists,
          githubRecipes: clearListsByPattern(state.lists.githubRecipes, pattern)
        }
      };
    default:
      return state;
  }
};

const handleLoadStaticContentSuccess = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.loadStaticContentSuccess>
): RecipeBaseState => {
  return {
    ...state,
    staticContent: action.data
  };
};

const handleClearCache = (
  state: RecipeBaseState,
  action: ReturnType<typeof RecipeBaseActions.clearCache>
): RecipeBaseState => {
  const { cacheType } = action;

  if (cacheType === 'all') {
    return initialState;
  }

  switch (cacheType) {
    case 'recipes':
      return {
        ...state,
        entities: { ...state.entities, recipes: {} },
        slugMaps: { ...state.slugMaps, recipes: {} },
        lists: { ...state.lists, recipes: {} }
      };
    case 'categories':
      return {
        ...state,
        entities: { ...state.entities, categories: {} },
        slugMaps: { ...state.slugMaps, categories: {} },
        lists: { ...state.lists, categories: {} }
      };
    case 'languageFrameworks':
      return {
        ...state,
        entities: { ...state.entities, languageFrameworks: {} },
        slugMaps: { ...state.slugMaps, languageFrameworks: {} },
        lists: { ...state.lists, languageFrameworks: {} }
      };
    case 'github':
      return {
        ...state,
        entities: { ...state.entities, githubRecipes: {} },
        slugMaps: { ...state.slugMaps, githubRecipes: {} },
        lists: { ...state.lists, githubRecipes: {} }
      };
    default:
      return state;
  }
};

export const recipeBaseState = createFeature({
  name: FEATURE_NAME,
  reducer: createReducer(
    initialState,
    on(RecipeBaseActions.loadRecipesSuccess, handleLoadRecipesSuccess),
    on(RecipeBaseActions.loadRecipeCategoriesSuccess, handleLoadRecipeCategoriesSuccess),
    on(RecipeBaseActions.loadRecipeLanguageFrameworksSuccess, handleLoadRecipeLanguageFrameworksSuccess),
    on(RecipeBaseActions.loadGithubRecipeSuccess, handleLoadGitHubRecipeSuccess),
    on(RecipeBaseActions.loadRelatedRecipesSuccess, handleLoadRelatedRecipesSuccess),
    // Cancel handlers - these actions end the loading state without changing data
    on(RecipeBaseActions.loadRecipesCancel, (state) => state),
    on(RecipeBaseActions.loadRecipeCategoriesCancel, (state) => state),
    on(RecipeBaseActions.loadRecipeLanguageFrameworksCancel, (state) => state),
    on(RecipeBaseActions.loadGithubRecipeCancel, (state) => state),
    on(RecipeBaseActions.loadRelatedRecipesCancel, (state) => state),
    // Cache invalidation handlers
    on(RecipeBaseActions.invalidateCache, handleInvalidateCache),
    on(RecipeBaseActions.clearCache, handleClearCache),
    // Static content handlers
    on(RecipeBaseActions.loadStaticContentSuccess, handleLoadStaticContentSuccess),
    on(RecipeBaseActions.loadStaticContentCancel, (state) => state)
  ),
  extraSelectors: ({ selectEntities, selectSlugMaps, selectLists, selectStaticContent }) => {
    // Helper selectors for direct list access
    const selectRecipeLists = createSelector(
      selectLists,
      lists => lists.recipes
    );

    const selectCategoryLists = createSelector(
      selectLists,
      lists => lists.categories
    );

    const selectLanguageFrameworkLists = createSelector(
      selectLists,
      lists => lists.languageFrameworks
    );

    return {
      // Direct selectors to access the list types
      selectRecipeLists,
      selectCategoryLists,
      selectLanguageFrameworkLists,

      // Original API compatibility for list selectors
      selectRecipeList: (key = 'default') => createSelector(
        selectRecipeLists,
        selectEntities,
        (recipeLists, entities) => {
          const ids = recipeLists[key] || [];
          return denormalize(ids, entities.recipes);
        }
      ),

      selectCategoryList: (key = 'default') => createSelector(
        selectCategoryLists,
        selectEntities,
        (categoryLists, entities) => {
          const ids = categoryLists[key] || [];
          return denormalize(ids, entities.categories);
        }
      ),

      selectLanguageFrameworkList: (key = 'default') => createSelector(
        selectLanguageFrameworkLists,
        selectEntities,
        (languageFrameworkLists, entities) => {
          const ids = languageFrameworkLists[key] || [];
          return denormalize(ids, entities.languageFrameworks);
        }
      ),

      // Alternative naming selectors
      selectRecipesByKey: (key = 'default') => createSelector(
        selectRecipeLists,
        selectEntities,
        (recipeLists, entities) => {
          const ids = recipeLists[key] || [];
          return denormalize(ids, entities.recipes);
        }
      ),

      selectCategoriesByKey: (key = 'default') => createSelector(
        selectCategoryLists,
        selectEntities,
        (categoryLists, entities) => {
          const ids = categoryLists[key] || [];
          return denormalize(ids, entities.categories);
        }
      ),

      selectLanguageFrameworksByKey: (key = 'default') => createSelector(
        selectLanguageFrameworkLists,
        selectEntities,
        (languageFrameworkLists, entities) => {
          const ids = languageFrameworkLists[key] || [];
          return denormalize(ids, entities.languageFrameworks);
        }
      ),

      // Entity by slug selectors
      selectRecipeBySlug: (slug: string) => createSelector(
        selectEntities,
        selectSlugMaps,
        (entities, slugMaps) => {
          const id = slugMaps.recipes[slug];
          return id ? entities.recipes[id] : undefined;
        }
      ),

      selectCategoryBySlug: (slug: string) => createSelector(
        selectEntities,
        selectSlugMaps,
        (entities, slugMaps) => {
          const id = slugMaps.categories[slug];
          return id ? entities.categories[id] : undefined;
        }
      ),

      selectLanguageFrameworkBySlug: (slug: string) => createSelector(
        selectEntities,
        selectSlugMaps,
        (entities, slugMaps) => {
          const id = slugMaps.languageFrameworks[slug];
          return id ? entities.languageFrameworks[id] : undefined;
        }
      ),

      // Maintain backward compatibility with original entityBySlug selector
      selectEntityBySlug: (slug: string) => createSelector(
        selectEntities,
        selectSlugMaps,
        (entities, slugMaps) => {
          const id = slugMaps.recipes[slug];
          return id ? entities.recipes[id] : undefined;
        }
      ),

      // Entity by ID selectors
      selectRecipeById: (id: string | number) => createSelector(
        selectEntities,
        (entities) => entities.recipes[id]
      ),

      selectCategoryById: (id: string | number) => createSelector(
        selectEntities,
        (entities) => entities.categories[id]
      ),

      selectLanguageFrameworkById: (id: string | number) => createSelector(
        selectEntities,
        (entities) => entities.languageFrameworks[id]
      ),

      // Original API compatibility for entity by ID
      selectEntityById: (id: string | number) => createSelector(
        selectEntities,
        (entities) => entities.recipes[id]
      ),

      // GitHub recipe selectors
      selectGitHubRecipeById: (id: string) => createSelector(
        selectEntities,
        (entities) => entities.githubRecipes[id]
      ),

      selectGitHubRecipeByUrl: (url: string) => createSelector(
        selectEntities,
        selectSlugMaps,
        (entities, slugMaps) => {
          const id = slugMaps.githubRecipes[url];
          return id ? entities.githubRecipes[id] : undefined;
        }
      ),

      // Simple loading state helpers - infer from data structure
      selectRecipeListRaw: (cacheKey: string) => createSelector(
        selectRecipeLists,
        (recipeLists) => recipeLists[cacheKey] // undefined = never loaded, [] = empty, [...] = data
      ),

      // Related recipes selectors
      selectRelatedRecipeIds: (recipeId: string | number) => createSelector(
        selectEntities,
        (entities) => entities.relatedRecipes[recipeId] || []
      ),

      selectRelatedRecipes: (recipeId: string | number) => createSelector(
        selectEntities,
        (entities) => {
          const ids = entities.relatedRecipes[recipeId] || [];
          return ids
            .map((id) => entities.recipes[id])
            .filter((r): r is RecipeExtended => !!r);
        }
      ),

      // Static content selectors (environment guides, service type docs from CMS)
      selectStaticContentLoaded: createSelector(
        selectStaticContent,
        (content) => content !== null
      ),

      selectEnvironmentGuides: createSelector(
        selectStaticContent,
        (content) => content?.environmentGuides ?? null
      ),

      selectServiceTypeDocs: createSelector(
        selectStaticContent,
        (content) => content?.serviceTypeDocs ?? null
      ),

      selectPlatformDocs: createSelector(
        selectStaticContent,
        (content) => content?.platformDocs ?? null
      )
    };
  }
});
